import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9b1igsngs93pt5sh740';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setSubmitStatus('success');
      setEmail('');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt' || !String(value).trim()) {
          params.append(key, String(value));
        }
      });

      const response = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const isSpam = responseText.toLowerCase().includes('spam');
      if (isSpam) {
        setFormError('Invio non riuscito. Riprova.');
        setSubmitStatus('error');
        return;
      }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || 'Errore sconosciuto';
        setFormError(serverMsg);
        setSubmitStatus('error');
      }
    } catch (_err) {
      setFormError('Si è verificato un errore. Riprova più tardi.');
      setSubmitStatus('error');
    }
  };

  const displayedCategories = categories.slice(0, 7);
  const moreCategories = categories.slice(7);

  return (
    <footer className="bg-foreground-900 text-background-50">
      <div className="w-full px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <span className="font-heading text-2xl font-semibold text-background-50 tracking-tight">
                Itaobuy <span className="text-primary-400">Spread</span>
              </span>
            </Link>
            <p className="text-foreground-300 text-sm leading-relaxed mb-6">
              {t('footer.brand_desc')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-9 h-9 rounded-full bg-foreground-800 flex items-center justify-center hover:bg-primary-500 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-background-50 text-sm w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">
              {t('footer.quick_links')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-foreground-300 text-sm hover:text-background-50 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/articolo/guida-completa-itaobuy-spreadsheet" className="text-foreground-300 text-sm hover:text-background-50 transition-colors">
                  {t('footer.guide')}
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-300 text-sm hover:text-background-50 transition-colors">
                  Itaobuy Sheet
                </a>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-300 text-sm hover:text-background-50 transition-colors">
                  Buy It Now
                </a>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">
              {t('footer.categories_footer')}
            </h4>
            <ul className="space-y-2.5">
              {displayedCategories.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-300 text-sm hover:text-background-50 transition-colors"
                  >
                    {t(cat.nameKey)}
                  </a>
                </li>
              ))}
              {moreCategories.length > 0 && (
                <li>
                  <span className="text-foreground-400 text-xs">
                    +{moreCategories.length} altre categorie
                  </span>
                </li>
              )}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">
              {t('footer.newsletter')}
            </h4>
            <p className="text-foreground-300 text-sm mb-4">
              {t('footer.newsletter_desc')}
            </p>
            <form onSubmit={handleSubmit} data-readdy-form="">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.email_placeholder')}
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg bg-foreground-800 border border-foreground-700 text-background-50 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="website-alt-field"
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-lg bg-primary-500 text-white font-label text-sm font-medium hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('footer.subscribe')}
                  <i className="ri-arrow-right-line ml-1.5 w-4 h-4 inline-flex items-center justify-center"></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-accent-400 text-xs mt-2">Grazie per l'iscrizione!</p>
              )}
              {submitStatus === 'error' && formError && (
                <p className="text-red-400 text-xs mt-2">{formError}</p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-400 text-xs">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-foreground-400 text-xs hover:text-background-50 transition-colors">
              Home
            </Link>
            <Link to="/articolo/guida-completa-itaobuy-spreadsheet" className="text-foreground-400 text-xs hover:text-background-50 transition-colors">
              Guida
            </Link>
            <span className="text-foreground-400 text-xs">
              Aggiornato: Luglio 2026
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}